/**
 * Widget IIFE entry point.
 *
 * When this bundle loads on a Shopify page, it auto-mounts the ChatShell
 * into a div with id="mc-sleep-advisor". If the div is absent, it creates
 * and appends one to document.body so embedding is always safe.
 *
 * The widget renders inside a Shadow DOM so its Tailwind styles (including the
 * preflight reset) stay fully contained and cannot affect the host Shopify
 * theme. The compiled CSS is inlined into the bundle and injected into the
 * shadow root at runtime, so the widget is a single self-contained file.
 *
 * The API base URL is read from data-api-url on the mount div so the widget
 * can be hosted on Netlify while the Shopify page is on mattresscrafters.com.au.
 *
 * Usage in Shopify Liquid:
 *   <div id="mc-sleep-advisor" data-api-url="https://mc-sleep-advisor.netlify.app"></div>
 *   <script src="https://mc-sleep-advisor.netlify.app/widget.js" defer></script>
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ChatShell } from "./components/chat/ChatShell";
import { setApiBaseUrl } from "./config";
import widgetCss from "./index.css?inline";

function mountWidget() {
  let host = document.getElementById("mc-sleep-advisor");

  if (!host) {
    // Fallback: create the mount node if it does not exist in the page
    host = document.createElement("div");
    host.id = "mc-sleep-advisor";
    document.body.appendChild(host);
  }

  // Guard against double-mounting if the script runs more than once
  if (host.dataset.mcMounted === "1") {
    return;
  }
  host.dataset.mcMounted = "1";

  // Read API base URL from data-attribute so cross-origin calls work from Shopify
  const apiBase = host.getAttribute("data-api-url");
  if (apiBase) {
    setApiBaseUrl(apiBase);
  }

  // Shadow DOM isolates the widget's Tailwind styles from the host theme
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = widgetCss;
  shadow.appendChild(style);

  const reactRoot = document.createElement("div");
  reactRoot.style.width = "100%";
  shadow.appendChild(reactRoot);

  createRoot(reactRoot).render(
    <StrictMode>
      <ChatShell />
    </StrictMode>,
  );
}

// Mount immediately if DOM is ready, otherwise wait for it
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountWidget);
} else {
  mountWidget();
}
